import React, { useEffect } from 'react';
import type { EditorialPropertyContext } from './editorialBlockProperties';
import { storageService } from '../../../services/storageService';
import { useAuth } from '../../../contexts/AuthContext';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { normalizeFooterTemplate } from '../utils/footerBlockUtils';

function LinksEditor({
  links,
  onAdd,
  onUpdate,
  onRemove,
}: {
  links: Array<{ text: string; url?: string }>;
  onAdd: () => void;
  onUpdate: (index: number, field: 'text' | 'url', value: string) => void;
  onRemove: (index: number) => void;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-slate-700">Links</label>
        <button type="button" onClick={onAdd} className="text-xs text-slate-800 hover:text-slate-700 font-medium">
          + Add Link
        </button>
      </div>
      {links.map((link, index) => (
        <div key={index} className="flex gap-2 mb-2 min-w-0">
          <input
            type="text"
            value={link.text || ''}
            onChange={(e) => onUpdate(index, 'text', e.target.value)}
            placeholder="Link text"
            className="flex-1 min-w-0 px-3 py-2 border border-slate-300 rounded-lg text-sm"
          />
          <input
            type="url"
            value={link.url || '#'}
            onChange={(e) => onUpdate(index, 'url', e.target.value)}
            placeholder="URL"
            className="flex-1 min-w-0 px-3 py-2 border border-slate-300 rounded-lg text-sm"
          />
          <button type="button" onClick={() => onRemove(index)} className="px-2 text-red-600 flex-shrink-0">
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

function SocialEditor({
  platforms,
  iconStyle,
  onAdd,
  onUpdate,
  onRemove,
  onIconStyleChange,
  onCustomUpload,
}: {
  platforms: any[];
  iconStyle: string;
  onAdd: () => void;
  onUpdate: (index: number, field: string, value: any) => void;
  onRemove: (index: number) => void;
  onIconStyleChange: (value: string) => void;
  onCustomUpload: (e: React.ChangeEvent<HTMLInputElement>, index: number) => void;
}) {
  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-1.5">
        <h5 className="text-xs font-semibold text-slate-800">Social Media</h5>
        <button type="button" onClick={onAdd} className="text-xs text-slate-800 font-medium">
          + Add Platform
        </button>
      </div>
      <select
        value={iconStyle}
        onChange={(e) => onIconStyleChange(e.target.value)}
        className="w-full mb-2 px-2 py-1.5 border border-slate-300 rounded-lg text-xs"
      >
        <option value="filled">Filled icons</option>
        <option value="outline">Outline icons</option>
      </select>
      {platforms.map((platform, index) => (
        <div key={index} className="mb-2">
          <div className="flex gap-1.5 items-center min-w-0">
            <select
              value={platform.icon || 'facebook'}
              onChange={(e) => onUpdate(index, 'icon', e.target.value)}
              className="px-2 py-1.5 border border-slate-300 rounded-lg text-xs min-w-[100px]"
            >
              <option value="facebook">Facebook</option>
              <option value="x">X</option>
              <option value="instagram">Instagram</option>
              <option value="linkedin">LinkedIn</option>
              <option value="youtube">YouTube</option>
              <option value="custom">Custom</option>
            </select>
            <input
              type="url"
              value={platform.url || ''}
              onChange={(e) => onUpdate(index, 'url', e.target.value)}
              placeholder="URL"
              className="flex-1 min-w-0 px-2 py-1.5 text-xs border border-slate-300 rounded-lg"
            />
            <button type="button" onClick={() => onRemove(index)} className="text-red-600 px-1">
              ×
            </button>
          </div>
          {platform.icon === 'custom' && (
            <input type="file" accept="image/*" onChange={(e) => onCustomUpload(e, index)} className="mt-1 text-xs w-full" />
          )}
        </div>
      ))}
    </div>
  );
}

export function FooterPropertiesPanel({ ctx }: { ctx: EditorialPropertyContext }) {
  const { currentUser } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const { block, handleContentChange } = ctx;

  const footerTemplate = normalizeFooterTemplate(block.content.template);
  const links = block.content.links || [];
  const platforms = block.content.socialPlatforms || [];
  const iconStyle = block.content.socialIconStyle || 'filled';

  useEffect(() => {
    if (block.content.template && block.content.template !== footerTemplate) {
      handleContentChange('template', footerTemplate);
    }
  }, [block.id, block.content.template, footerTemplate, handleContentChange]);

  const addLink = () => handleContentChange('links', [...links, { text: 'Link', url: '#' }]);
  const updateLink = (index: number, field: 'text' | 'url', value: string) => {
    const next = [...links];
    next[index] = { ...next[index], [field]: value };
    handleContentChange('links', next);
  };
  const removeLink = (index: number) => handleContentChange('links', links.filter((_: any, i: number) => i !== index));

  const addSocial = () =>
    handleContentChange('socialPlatforms', [...platforms, { name: 'Facebook', url: '#', icon: 'facebook' }]);
  const updateSocial = (index: number, field: string, value: any) => {
    const next = [...platforms];
    next[index] = { ...next[index], [field]: value };
    handleContentChange('socialPlatforms', next);
  };
  const removeSocial = (index: number) =>
    handleContentChange('socialPlatforms', platforms.filter((_: any, i: number) => i !== index));

  const handleCustomIconUpload = async (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !currentUser || !file.type.startsWith('image/')) return;
    try {
      const uploaded = await storageService.uploadFile(file, currentUser.uid, currentWorkspace?.id);
      updateSocial(index, 'customIconUrl', uploaded.url);
    } catch {
      alert('Failed to upload icon.');
    }
  };

  const showSocial = footerTemplate === 'complete' || footerTemplate === 'gradient' || footerTemplate === 'social';
  const showLinks = footerTemplate !== 'complete';

  return (
    <>
      <div className="space-y-3 border border-slate-200 rounded-lg p-4">
        <h5 className="text-sm font-semibold text-slate-800">Footer Design</h5>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Template</label>
          <select
            value={footerTemplate}
            onChange={(e) => handleContentChange('template', e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg"
          >
            <option value="standard">Standard</option>
            <option value="complete">Complete (company, apps, social)</option>
            <option value="minimal">Minimal</option>
            <option value="gradient">Gradient</option>
            <option value="social">Social Footer</option>
          </select>
        </div>
      </div>

      <div className="space-y-3 border border-slate-200 rounded-lg p-4 mt-4">
        <h5 className="text-sm font-semibold text-slate-800 mb-3">Content</h5>

        {(footerTemplate === 'standard' || footerTemplate === 'minimal' || footerTemplate === 'gradient') && (
          <div className="mb-3">
            <label className="block text-sm font-medium text-slate-700 mb-1">Footer Text</label>
            <textarea
              value={block.content.text || ''}
              onChange={(e) => handleContentChange('text', e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              rows={2}
            />
          </div>
        )}

        {(footerTemplate === 'complete' || footerTemplate === 'social') && (
          <>
            <div className="mb-3">
              <label className="block text-sm font-medium text-slate-700 mb-1">Company Name</label>
              <input
                type="text"
                value={block.content.companyName || ''}
                onChange={(e) => handleContentChange('companyName', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              />
            </div>
            {footerTemplate === 'social' && (
              <div className="mb-3">
                <label className="block text-sm font-medium text-slate-700 mb-1">Tagline</label>
                <input
                  type="text"
                  value={block.content.text || ''}
                  onChange={(e) => handleContentChange('text', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                />
              </div>
            )}
          </>
        )}

        {footerTemplate === 'complete' && (
          <>
            <div className="mb-3">
              <label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
              <textarea
                value={block.content.location || ''}
                onChange={(e) => handleContentChange('location', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                rows={2}
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium text-slate-700 mb-1">Disclaimer</label>
              <textarea
                value={block.content.disclaimer || ''}
                onChange={(e) => handleContentChange('disclaimer', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                rows={2}
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium text-slate-700 mb-1">App section heading</label>
              <input
                type="text"
                value={block.content.appHeading || ''}
                onChange={(e) => handleContentChange('appHeading', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium text-slate-700 mb-1">App Store URL</label>
              <input
                type="url"
                value={block.content.appStoreUrl || ''}
                onChange={(e) => handleContentChange('appStoreUrl', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium text-slate-700 mb-1">Google Play URL</label>
              <input
                type="url"
                value={block.content.googlePlayUrl || ''}
                onChange={(e) => handleContentChange('googlePlayUrl', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              />
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div>
                <label className="block text-xs text-slate-600 mb-1">Icon color</label>
                <input
                  type="color"
                  value={block.content.socialIconColor || '#FFFFFF'}
                  onChange={(e) => handleContentChange('socialIconColor', e.target.value)}
                  className="w-full h-9 border border-slate-300 rounded"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">Icon background</label>
                <input
                  type="color"
                  value={block.content.socialBgColor || '#111827'}
                  onChange={(e) => handleContentChange('socialBgColor', e.target.value)}
                  className="w-full h-9 border border-slate-300 rounded"
                />
              </div>
            </div>
          </>
        )}

        {showLinks && (
          <LinksEditor links={links} onAdd={addLink} onUpdate={updateLink} onRemove={removeLink} />
        )}

        {showSocial && (
          <SocialEditor
            platforms={platforms}
            iconStyle={iconStyle}
            onAdd={addSocial}
            onUpdate={updateSocial}
            onRemove={removeSocial}
            onIconStyleChange={(v) => handleContentChange('socialIconStyle', v)}
            onCustomUpload={handleCustomIconUpload}
          />
        )}

      </div>
    </>
  );
}
